/**
 * @file LorryDayWindow.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-23
 * @description Lorry day planning — one lorry's day, as the planner enters it
 *              (bulk board, transport_day_plan switched on; replaces Loads and
 *              Drops' Day window). Opens from a lorry's group on the board.
 *
 *                1. The day: every collection and drop in time order. Type a
 *                   stop's time, or drag it (Alt+↑/↓ by keyboard) and it takes
 *                   a time between its neighbours. Drops sharing a time keep
 *                   the order they are put in.
 *                2. The lorry at the chosen stop — the lorry drawing
 *                   (LorryDrawing: rigid, artic, bag): drag a door to any whole
 *                   tonne, click a door to open or close it, type a size; pick
 *                   a product and click the compartment(s) it goes in. A door
 *                   layout applies from the stop's time until the next.
 *                3. The day at a glance (a strip per stop) and the checks —
 *                   guidance only, never a refusal.
 *              ↑ / ↓ move between the stops' time fields; each product shows
 *              the planners' own note, if it has one.
 *
 *              Every change is saved as it is made (1.4.0) through PUT
 *              /api/transport/planning/lorry-day — one transaction, one Plan
 *              History entry per save — a moment (SAVE_AFTER_MS, 800 ms)
 *              after the last change, so a door dragged or a time typed is
 *              one save, not one per step. "Suggest" fills only what is
 *              blank, and is saved like any change. Read-only — greyed,
 *              never hidden — on a locked or past day, a snapshot, without
 *              transport_planning, or while someone else plans the day.
 *
 *              1.4.0 (2026-10-23, Cam: "can the 'Day view' auto save on
 *              change, like the main view does?"): no Save button. The
 *              footer says where saving has got to — saving in a moment /
 *              saving / all changes saved (time) / not saved: <why>, with
 *              "Try again"; a save that fails (a locked day, planning control
 *              lost, the network) keeps the edit on screen. Close saves what
 *              is left first and closes once it is saved (a failure keeps the
 *              window open and asks); leaving the screen or the day saves it
 *              too, and a tab closing sends it with keepalive and asks first.
 *              Each save sends only what differs from what was last saved —
 *              the door layouts only when they changed.
 *
 *              1.3.0 (2026-10-15, Cam): each stop's orders and the products
 *              to place are listed first drop first; a collection time typed
 *              fills the drop times that are blank or earlier; compartment
 *              sizes are typed in whole tonnes and change only that
 *              compartment — the sizes may add up to more than the payload,
 *              said above the drawing and in the checks, never refused; the
 *              drawing stays inside the lorry's body (LorryDrawing 1.1.0).
 *
 *              1.1.0 (2026-10-12): a self-billed PO's rows (no order line —
 *              utils/selfBilledPo) are on the day as collect and drop stops,
 *              but their drop time is not set here and they are never put in
 *              a compartment (nothing to keep either on); their collection
 *              time is set as any row's.
 *              1.2.0 (2026-10-13, Cam): once the database keeps a PO line's
 *              plan, a self-billed row is planned here like any other; only
 *              a row stamped no_line_plan (before that update) is held back.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { dayRowKey, sortByDropOrder } from '../utils/lorryDayOrder';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import ProgressRing from './ProgressRing';
import LorryDrawing from './LorryDrawing';
import PlanTimeInput from './PlanTimeInput';
import { useSlideOutButton } from './SlideOutButton';
import {
  buildLorryDay, suggestLorryDay, normaliseLinePlan, normaliseLorryDay, emptyLorryDay, normaliseTime,
  timeBetween, resizeCompartment, compartmentLabel, lorryLabel, minutesOf,
  dropTimeAfterCollect, layoutTotalCheck, wholeTonnes
} from '../utils/lorryDay';
import { SEVERITY_COLOR } from '../utils/vehicleLoad';
import { formatQuantity } from '../utils/numberFormatters';
import './LorryDayWindow.css';

const t2 = (n) => (Number.isFinite(Number(n)) ? formatQuantity(Number(n)) : '—');
const COLOURS = 8;
const sevColor = (sev) => (sev === 'info' ? 'var(--color-info)' : (SEVERITY_COLOR[sev] || 'var(--text-muted)'));
const sevIcon = (sev) => (sev === 'warn' ? 'warning' : sev === 'info' ? 'info' : sev === 'muted' ? 'help' : 'check_circle');
const clone = (v) => JSON.parse(JSON.stringify(v));

/** The draft a window starts from: the rows' times and plans, the lorry's layouts. */
function startDraft(rows, lorryDay) {
  return {
    collect: Object.fromEntries(rows.map(r => [String(r.order_line_id), normaliseTime(r.scheduled_time)])),
    lines: Object.fromEntries(rows.map(r => [String(r.order_line_id), normaliseLinePlan(r.line_plan)])),
    lorryDay: normaliseLorryDay(lorryDay).value || emptyLorryDay()
  };
}

/*
 * A stop's key carries its time, so it changes when the time does. The window
 * holds a stop by what it is instead — collect or drop, and one of its order
 * lines — so the chosen stop and the keyboard focus follow it when it moves.
 */
const idOf = (s) => (s ? { kind: s.kind, rowId: String(s.rowIds[0]) } : null);
const findStop = (day, id) => (id ? day.stops.find(s => s.kind === id.kind && s.rowIds.map(String).includes(id.rowId)) : null);

/**
 * One compartment's size, typed in whole tonnes (Cam, 2026-10-15: "never
 * 2.77T"): saved on leaving the field or Enter, rounded to the nearest whole
 * tonne (at least 1 T); only this compartment changes.
 */
function SizeInput({ n, size, disabled, title, onCommit }) {
  const [text, setText] = useState(null);
  const commit = () => {
    if (text === null) return;
    const typed = Number(String(text).replace(',', '.'));
    setText(null);
    if (!Number.isFinite(typed) || typed <= 0) return;
    const v = wholeTonnes(typed);
    if (v !== size) onCommit(v, typed);
  };
  return (
    <label className="ldw-size" htmlFor={`ldw-size-${n}`}>
      C{n}
      <input
        id={`ldw-size-${n}`}
        type="number"
        min="1"
        step="1"
        inputMode="numeric"
        className="ldw-size-input"
        value={text !== null ? text : size}
        disabled={disabled}
        title={title}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
      />
      <span>T</span>
    </label>
  );
}

function LorryDayBody({ day, draft, setDraft, vehicle, canEdit, readOnlyReason, saving, dirty, message, setMessage, unknownPayload, onSetUnknownPayload }) {
  const [selected, setSelected] = useState(() => idOf(day.stops[0]));
  const [picked, setPicked] = useState(null);
  const [dragKey, setDragKey] = useState(null);
  const [payloadDraft, setPayloadDraft] = useState(unknownPayload != null ? String(unknownPayload) : '');
  const focusKey = useRef(null);
  const listRef = useRef(null);
  useSlideOutButton([saving, dirty]);
  useEffect(() => {
    if (!focusKey.current || !listRef.current) return;
    const target = findStop(day, focusKey.current);
    const el = target && [...listRef.current.querySelectorAll('[data-stop]')].find(li => li.getAttribute('data-stop') === target.key);
    focusKey.current = null;
    if (el) el.focus();
  }, [day]);

  const stop = findStop(day, selected) || day.stops[0] || null;
  const colourOf = useMemo(() => {
    const m = new Map(day.rowOrder.map((id, i) => [String(id), i % COLOURS]));
    return (id) => m.get(String(id)) ?? 0;
  }, [day.rowOrder]);
  const rowInfo = (id) => {
    const r = day.rows[String(id)] || {};
    return { name: r.customerName || '—', product: r.productName || '', colour: colourOf(id) };
  };
  const editTitle = canEdit ? undefined : readOnlyReason;
  // A stop's orders, and the products to place, first drop first (Cam,
  // 2026-10-15: "first drop top of the list, last drop bottom").
  const byDrop = (ids) => sortByDropOrder(ids.map(String), (id) => dayRowKey(day.rows[id]));
  // A self-billed PO's row keeps no line plan: no drop time, no compartment.
  const noPlan = (id) => !!day.rows[String(id)]?.noLinePlan;
  const untimedDrop = (s) => !!s && s.kind === 'drop' && s.rowIds.length > 0 && s.rowIds.every(noPlan);
  const UNTIMED_DROP = 'A self-billed PO’s drop time is kept once a database update has been applied.';

  // ── Times ──────────────────────────────────────────────────────────────
  const setStopTime = (s, time, seq = undefined) => {
    const next = clone(draft);
    for (const id of s.rowIds) {
      if (s.kind === 'collect') next.collect[id] = time;
      else if (noPlan(id)) continue;
      else {
        next.lines[id] = { ...next.lines[id], dropTime: time };
        if (seq !== undefined) next.lines[id].dropSeq = seq;
      }
    }
    return next;
  };
  const commitTime = (s, time) => {
    setMessage(null);
    const next = setStopTime(s, time, null);
    // A collection time typed fills each of its rows' drop times that are
    // blank or earlier (Cam, 2026-10-15) — still typed over as wanted.
    if (s.kind === 'collect' && time) {
      let filled = 0;
      for (const id of s.rowIds) {
        if (noPlan(id)) continue;
        const drop = dropTimeAfterCollect(next.lines[id]?.dropTime, time);
        if (drop) { next.lines[id] = { ...next.lines[id], dropTime: drop }; filled += 1; }
      }
      if (filled) setMessage({ kind: 'info', text: `Drop time set to ${time} for ${filled === 1 ? 'the order' : `${filled} orders`} collected here — change it if they drop later.` });
    }
    setDraft(next);
  };

  /** Move a stop so it sits at `index` among the others: it takes a time between its new neighbours. */
  const moveTo = (key, index) => {
    const s = day.stops.find(x => x.key === key);
    if (!s) return;
    if (untimedDrop(s)) { setMessage({ kind: 'info', text: UNTIMED_DROP }); return; }
    const others = day.stops.filter(x => x.key !== key);
    const prev = others[index - 1] || null;
    const next = others[index] || null;
    const { time, tie } = timeBetween(prev && prev.time ? prev : null, next && next.time ? next : null);
    if (!time) { setMessage({ kind: 'error', text: 'Give the stops around it a time first — a stop moves to a time between its neighbours.' }); return; }
    let draftNext = setStopTime(s, time, null);
    if (tie && s.kind === 'drop') {
      // Drops sharing a time keep the order they are put in (drop_seq).
      const sameTime = others.filter(x => x.kind === 'drop' && x.time === time);
      const at = sameTime.findIndex(x => x.key === tie.key);
      const order = [...sameTime.slice(0, at + 1), s, ...sameTime.slice(at + 1)];
      order.forEach((x, i) => {
        for (const id of x.rowIds) draftNext.lines[id] = { ...draftNext.lines[id], dropSeq: i + 1 };
      });
    } else if (tie && s.kind === 'collect') {
      setMessage({ kind: 'info', text: `Collections at ${time} keep the board’s order. Give this one its own time to put it first.` });
    } else {
      setMessage(null);
    }
    setDraft(draftNext);
  };
  const dropOn = (targetKey) => {
    const from = dragKey;
    setDragKey(null);
    if (!canEdit || !from || from === targetKey) return;
    const others = day.stops.filter(x => x.key !== from);
    let idx = targetKey == null ? others.length : others.findIndex(x => x.key === targetKey);
    if (idx < 0) idx = others.length;
    // Dragged down onto a stop: it goes after it; dragged up: before it.
    const fromAt = day.stops.findIndex(x => x.key === from);
    const targetAt = day.stops.findIndex(x => x.key === targetKey);
    if (targetKey != null && fromAt < targetAt) idx += 1;
    moveTo(from, idx);
  };
  const nudge = (s, step) => {
    const i = day.stops.findIndex(x => x.key === s.key);
    const j = i + step;
    if (j < 0 || j >= day.stops.length) return;
    focusKey.current = idOf(s);
    moveTo(s.key, j);
  };

  // ── Doors and compartments, at the chosen stop ────────────────────────
  const body = day.body;
  const firstStop = day.stops[0];
  const layoutFor = (s) => (s && s.time ? s.time : (s && firstStop && s.key === firstStop.key ? null : undefined));
  const changeLayout = (change) => {
    if (!stop) return;
    const from = layoutFor(stop);
    if (from === undefined) { setMessage({ kind: 'error', text: 'Give this stop a time first — a door layout applies from a time.' }); return; }
    const cur = stop.layout || { sizes: [], open: [] };
    const nextLayout = change({ sizes: cur.sizes.slice(), open: cur.open.slice() });
    const next = clone(draft);
    next.lorryDay.layouts = [
      ...next.lorryDay.layouts.filter(l => (l.from || null) !== from),
      { from, sizes: body.moveable ? nextLayout.sizes : null, open: [...new Set(nextLayout.open)].sort((a, b) => a - b) }
    ].sort((a, b) => (minutesOf(a.from) ?? -1) - (minutesOf(b.from) ?? -1));
    setMessage(null);
    setDraft(next);
  };
  const ownLayout = stop ? draft.lorryDay.layouts.find(l => (l.from || null) === layoutFor(stop)) : null;
  const clearLayout = () => {
    const from = layoutFor(stop);
    const next = clone(draft);
    next.lorryDay.layouts = next.lorryDay.layouts.filter(l => (l.from || null) !== from);
    setDraft(next);
  };

  // A click on the drawing: the compartments of the space clicked (one, or
  // several joined by open doors). Put the chosen product in them all, or
  // take it out of them all when it is already in every one.
  const toggleCompartment = (nums) => {
    const ns = Array.isArray(nums) ? nums : [nums];
    if (picked == null) {
      const c = (stop?.compartments || []).find(x => ns.includes(x.n) && x.items.length);
      if (c && c.items.length) {
        setPicked(String(c.items[0].rowId));
        const r = rowInfo(c.items[0].rowId);
        setMessage({ kind: 'info', text: `${r.name} · ${r.product} chosen — click compartments to put it in or take it out.` });
      } else {
        setMessage({ kind: 'info', text: 'Choose a product below first, then click the compartments it goes in.' });
      }
      return;
    }
    const next = clone(draft);
    const lp = next.lines[picked] || normaliseLinePlan(null);
    const all = ns.every(n => lp.compartments.includes(n));
    lp.compartments = all
      ? lp.compartments.filter(x => !ns.includes(x))
      : [...new Set([...lp.compartments, ...ns])].sort((a, b) => a - b);
    next.lines[picked] = lp;
    setDraft(next);
  };
  const clearCompartments = (id) => {
    const next = clone(draft);
    next.lines[id] = { ...(next.lines[id] || normaliseLinePlan(null)), compartments: [] };
    setDraft(next);
  };

  // Products to place at this stop: those collected here, and those on board after it.
  const placeable = (() => {
    if (!stop) return [];
    const onBoard = new Set((stop.compartments || []).flatMap(c => c.items.map(i => String(i.rowId))));
    const here = stop.rowIds.map(String);
    const ids = [...new Set([...here, ...onBoard])];
    // Rows aboard with no compartment are not in the drawing: find them by the walk.
    const idx = day.stops.findIndex(x => x.key === stop.key);
    for (const id of Object.keys(day.rows)) {
      const r = day.rows[id];
      const ci = day.stops.findIndex(x => x.key === r.collectKey);
      const di = day.stops.findIndex(x => x.key === r.dropKey);
      if (ci <= idx && di > idx && !ids.includes(id)) ids.push(id);
    }
    // A self-billed PO's rows take no compartment.
    return byDrop(ids.filter(id => !noPlan(id)).sort((a, b) => day.rowOrder.indexOf(a) - day.rowOrder.indexOf(b)));
  })();

  const checksAt = stop ? stop.checks : [];
  // Over-filled: the layout's sizes over the payload, or more on board than it.
  const totalCheck = stop && stop.layout ? layoutTotalCheck(body, stop.layout.sizes) : null;
  const overCheck = totalCheck && totalCheck.severity === 'warn' ? totalCheck : null;
  const overweight = checksAt.find(c => c.code === 'overweight') || null;
  const commitPayload = () => {
    if (!onSetUnknownPayload) return;
    const v = payloadDraft.trim();
    onSetUnknownPayload(v === '' ? null : Number(v));
  };

  return (
    <div className="ldw-body">
      <div className="ldw-summary">
        <span className="material-symbols-rounded" style={{ color: sevColor(day.severity) }}>{sevIcon(day.severity)}</span>
        <span>{day.collectCount} collection{day.collectCount === 1 ? '' : 's'} · {day.dropCount} drop{day.dropCount === 1 ? '' : 's'}</span>
        {body.cap != null && <span className="ldw-muted">Most on board {t2(day.peak.tonnes)} / {t2(body.cap)} T</span>}
        {day.efficiency != null && <ProgressRing percentage={day.efficiency} size={40} color={sevColor(day.efficiency < 40 ? 'warn' : day.efficiency < 60 ? 'warn' : 'ok')} />}
        {!canEdit && <span className="ldw-readonly">{readOnlyReason}</span>}
      </div>
      {message && <div className={`ldw-message ldw-message-${message.kind}`} role={message.kind === 'error' ? 'alert' : 'status'}>{message.text}</div>}

      <div className="ldw-grid">
        {/* 1. The day */}
        <section className="ldw-panel ldw-day">
          <h3>The day</h3>
          <ol className="ldw-stops" aria-label="Stops" ref={listRef} data-time-grid>
            {day.stops.map((s, i) => (
              <li
                key={s.key}
                data-stop={s.key}
                className={`ldw-stop ldw-stop-${s.kind}${s.key === stop?.key ? ' selected' : ''}${dragKey === s.key ? ' dragging' : ''}`}
                draggable={canEdit}
                tabIndex={0}
                aria-current={s.key === stop?.key ? 'step' : undefined}
                title={canEdit ? 'Drag (or Alt+↑/↓) to move it — it takes a time between its neighbours; click to see the lorry here' : editTitle}
                onClick={() => { setSelected(idOf(s)); setPicked(null); }}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(idOf(s)); setPicked(null); return; }
                  if (canEdit && e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) { e.preventDefault(); nudge(s, e.key === 'ArrowUp' ? -1 : 1); }
                }}
                onDragStart={(e) => { if (!canEdit) return; setDragKey(s.key); if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={(e) => { if (canEdit) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); dropOn(s.key); }}
                onDragEnd={() => setDragKey(null)}
              >
                <PlanTimeInput
                  className="ldw-time"
                  navRow={s.key}
                  navCol={0}
                  value={s.time}
                  disabled={!canEdit || untimedDrop(s)}
                  title={!canEdit ? editTitle : (untimedDrop(s) ? UNTIMED_DROP : (s.kind === 'collect' ? 'Collection time' : 'Drop time'))}
                  ariaLabel={`${s.kind === 'collect' ? 'Collection' : 'Drop'} time, ${s.name}`}
                  onCommit={(t) => commitTime(s, t)}
                />
                <span className="ldw-kind material-symbols-rounded" title={s.kind === 'collect' ? 'Collect' : 'Drop'}>{s.kind === 'collect' ? 'upload' : 'download'}</span>
                <span className="ldw-stop-text">
                  <strong>{s.kind === 'collect' ? `Collect · ${s.name || 'supplier'}` : `Drop ${s.number} · ${s.name || 'customer'}`}</strong>
                  {byDrop(s.rowIds).map(id => {
                    const r = day.rows[String(id)];
                    return (
                      <span key={id} className="ldw-stop-line">
                        <i className={`ldw-dot ld-c${colourOf(id)}`} />
                        {r.productName} {t2(r.tonnes)} T{s.kind === 'collect' ? ` · ${r.customerName}` : ''}
                        {r.compartmentLabel && <span className="ldw-comp-tag">{r.compartmentLabel}</span>}
                        {r.note && <span className="ldw-note" title="Planner’s note — never printed or sent"><span className="material-symbols-rounded">sticky_note_2</span>{r.note}</span>}
                      </span>
                    );
                  })}
                </span>
                <span className="ldw-muted ldw-onboard" title="On board after this stop">{t2(s.onBoard)} T</span>
                <span className="material-symbols-rounded ldw-sev" style={{ color: sevColor(s.severity) }} title={s.checks.map(c => c.message).join('\n') || 'Nothing to look at'}>{sevIcon(s.severity)}</span>
                <span className="ldw-stop-move">
                  {[[-1, 'arrow_upward', 'Earlier'], [1, 'arrow_downward', 'Later']].map(([step, icon, label]) => {
                    const ok = canEdit && i + step >= 0 && i + step < day.stops.length;
                    return (
                      <button key={icon} type="button" aria-label={`${label}: ${s.label}`}
                        className={`btn-icon${ok ? '' : ' btn-disabled'}`} disabled={!ok}
                        title={!canEdit ? editTitle : (ok ? `${label} — takes a time between its new neighbours` : (step < 0 ? 'Already the first stop' : 'Already the last stop'))}
                        onClick={(e) => { e.stopPropagation(); nudge(s, step); }}>
                        <span className="material-symbols-rounded">{icon}</span>
                      </button>
                    );
                  })}
                </span>
              </li>
            ))}
          </ol>
          <div
            className={`ldw-stop-end${dragKey ? ' active' : ''}`}
            onDragOver={(e) => { if (canEdit) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); dropOn(null); }}
          >Drop here to make it the last stop</div>
        </section>

        {/* 2. The lorry at the chosen stop */}
        <section className="ldw-panel ldw-lorry">
          <h3>The lorry {stop ? `after ${stop.label}` : ''}</h3>
          {body.count ? (
            <>
              {(overCheck || overweight) && (
                <div className="ldw-over" role="note">
                  <span className="material-symbols-rounded">scale</span>
                  <span>
                    {[overCheck && overCheck.message, overweight && overweight.message].filter(Boolean).join(' · ')}.
                    {' '}Allowed — make sure it is what you mean before the paperwork goes out.
                  </span>
                </div>
              )}
              <LorryDrawing
                vehicle={vehicle}
                moveable={body.moveable}
                sizes={stop?.layout?.sizes || []}
                open={stop?.layout?.open || []}
                compartments={stop?.compartments || []}
                rowInfo={rowInfo}
                pickedRowId={picked}
                editable={canEdit}
                editTitle={editTitle}
                onSizes={(sizes) => changeLayout(l => ({ ...l, sizes }))}
                onToggleDoor={(d) => changeLayout(l => ({ ...l, open: l.open.includes(d) ? l.open.filter(x => x !== d) : [...l.open, d] }))}
                onCompartment={toggleCompartment}
                overNote={overweight ? `${t2(overweight.by)} T over payload`
                  : (overCheck ? `Compartments ${t2(overCheck.by)} T over payload` : null)}
              />
              <div className="ldw-sizes">
                <span className="ldw-label">{body.moveable ? 'Sizes' : 'Compartments'}</span>
                {(stop?.layout?.sizes || []).map((size, i) => (
                  <SizeInput
                    key={`${stop?.key}-${i}`}
                    n={i + 1}
                    size={size}
                    disabled={!canEdit || !body.moveable}
                    title={!body.moveable ? 'A fixed body keeps its own sizes — doors can still be opened' : (editTitle || 'Whole tonnes — the other compartments stay as they are')}
                    onCommit={(v, typed) => {
                      changeLayout(l => ({ ...l, sizes: resizeCompartment(l.sizes, i + 1, v) }));
                      if (typed !== v) setMessage({ kind: 'info', text: `Compartments are whole tonnes — C${i + 1} set to ${v} T.` });
                    }}
                  />
                ))}
                {body.moveable && stop?.layout?.sizes?.length > 0 && (
                  <span className={`ldw-total${totalCheck && totalCheck.severity === 'warn' ? ' over' : ''}`}
                    title={totalCheck ? totalCheck.message : 'The compartments add up to the lorry’s body'}>
                    = {t2(stop.layout.sizes.reduce((a, b) => a + b, 0))} T{body.cap != null ? ` of ${t2(body.cap)} T` : ''}
                  </span>
                )}
                <span className="ldw-muted">
                  {stop?.layout?.set
                    ? `Layout from ${stop.layout.from || 'the start of the day'}${stop.layout.open.length ? ` · door${stop.layout.open.length === 1 ? '' : 's'} ${stop.layout.open.join(', ')} open` : ''}`
                    : 'The lorry’s own layout — nothing set yet'}
                </span>
                {ownLayout && (
                  <button type="button" className={`btn-icon${canEdit ? '' : ' btn-disabled'}`} disabled={!canEdit}
                    title={canEdit ? 'Remove the layout set from this stop' : editTitle} onClick={clearLayout}>
                    <span className="material-symbols-rounded">restart_alt</span>
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="ldw-unknown">
              <span className="material-symbols-rounded">scale</span>
              <div>
                <label htmlFor="ldw-payload">Payload of this driver’s vehicle (T)</label>
                <input
                  id="ldw-payload"
                  type="number"
                  min="0"
                  step="0.1"
                  className="form-input"
                  value={payloadDraft}
                  disabled={!onSetUnknownPayload || !canEdit}
                  onChange={(e) => setPayloadDraft(e.target.value)}
                  onBlur={commitPayload}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitPayload(); } }}
                />
                <p className="ldw-muted">No compartments on this vehicle — only its payload is checked.</p>
              </div>
            </div>
          )}

          {body.count > 0 && (
            <div className="ldw-place">
              <h4>Products {stop?.kind === 'collect' ? 'collected here and on board' : 'on board'}</h4>
              {placeable.length === 0 ? <p className="ldw-muted">Nothing on board here.</p> : (
                <ul className="ldw-chips">
                  {placeable.map(id => {
                    const r = day.rows[id];
                    const on = picked === id;
                    return (
                      <li key={id} className={`ldw-chip${on ? ' picked' : ''}`}>
                        <button type="button" className="ldw-chip-main" disabled={!canEdit}
                          title={canEdit ? (on ? 'Chosen — click compartments in the lorry to put it in or take it out' : 'Choose this product, then click its compartments') : editTitle}
                          onClick={() => { setPicked(on ? null : id); setMessage(on ? null : { kind: 'info', text: `${r.customerName} · ${r.productName} chosen — click compartments to put it in or take it out.` }); }}>
                          <i className={`ldw-dot ld-c${colourOf(id)}`} />
                          <span>{r.customerName} · {r.productName} · {t2(r.tonnes)} T</span>
                          <span className="ldw-comp-tag">{r.compartmentLabel || 'no compartment'}</span>
                        </button>
                        <button type="button" className={`btn-icon${canEdit && r.compartments.length ? '' : ' btn-disabled'}`}
                          disabled={!canEdit || !r.compartments.length}
                          title={!canEdit ? editTitle : (r.compartments.length ? 'Take it out of its compartments' : 'Not in a compartment')}
                          onClick={() => clearCompartments(id)}>
                          <span className="material-symbols-rounded">backspace</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          <div className="ldw-checks">
            <h4>Checks {stop ? `at ${stop.label}` : ''}</h4>
            {checksAt.length === 0 ? <p className="ldw-ok">Nothing to look at here.</p> : (
              <ul>
                {checksAt.map((c, i) => (
                  <li key={i} style={{ color: sevColor(c.severity) }}>
                    <span className="material-symbols-rounded">{sevIcon(c.severity)}</span>
                    <span>{c.message}</span>
                  </li>
                ))}
              </ul>
            )}
            {day.checks.length > checksAt.length && (
              <p className="ldw-muted">
                Elsewhere in the day: {day.stops.filter(s => s.key !== stop?.key && s.checks.length).map(s => (
                  <button key={s.key} type="button" className="ldw-link" onClick={() => setSelected(idOf(s))}>{s.label}</button>
                ))}
              </p>
            )}
            <p className="ldw-muted ldw-guidance">Checks are guidance: they never stop a save, and never print on paperwork.</p>
          </div>
        </section>
      </div>

      {/* 3. The day at a glance */}
      <div className="ldw-film" aria-label="The lorry after each stop">
        {day.stops.map(s => (
          <button key={s.key} type="button" className={`ldw-frame${s.key === stop?.key ? ' selected' : ''}`} onClick={() => { setSelected(idOf(s)); setPicked(null); }} title={s.label}>
            <span className="ldw-frame-time">{s.time || '—:—'}</span>
            <span className="ldw-frame-what">{s.kind === 'collect' ? `Collect ${s.name}` : `Drop ${s.name}`}</span>
            <span className="ldw-strip">
              {body.count ? (s.compartments || []).map(c => {
                const first = c.items[0];
                return <i key={c.n} style={{ flex: c.size || 1 }} className={first ? `ld-c${colourOf(first.rowId)}` : ''} />;
              }) : <i style={{ flex: 1 }} className={s.onBoard > 0 ? 'ld-c0' : ''} />}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** What saving `d` sends, against what the server holds (`base`); null when nothing. */
function payloadFor(d, base, rows) {
  const lines = {};
  const collectTimes = {};
  // Notes are edited on the board, not here: never sent from the window, so
  // a note changed meanwhile by someone else is not put back.
  const withoutNote = (lp) => { const rest = { ...(lp || {}) }; delete rest.note; return rest; };
  const keepsPlan = new Map(rows.map(r => [String(r.order_line_id), !r.no_line_plan]));
  for (const id of Object.keys(d.lines)) {
    if (!keepsPlan.has(String(id))) continue; // a row no longer on this lorry
    const l = withoutNote(d.lines[id]);
    // A row that can keep no line plan yet (no_line_plan) — never sent (the server refuses one).
    if (keepsPlan.get(String(id)) !== false && JSON.stringify(l) !== JSON.stringify(withoutNote(base.lines[id]))) lines[id] = l;
    if ((d.collect[id] || null) !== ((base.collect || {})[id] || null)) collectTimes[id] = d.collect[id] || null;
  }
  const out = { lines, collectTimes };
  // The door layouts only when they changed: a save of a drop time never
  // re-sends (and so never overwrites) the layouts.
  if (JSON.stringify(d.lorryDay) !== JSON.stringify(base.lorryDay)) out.lorryDay = d.lorryDay;
  return Object.keys(lines).length || Object.keys(collectTimes).length || 'lorryDay' in out ? out : null;
}

/** Saved a moment after the last change (a door dragged, a time typed) — one save, one Plan History entry. */
const SAVE_AFTER_MS = 800;
const hhmm = (ms) => {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * @param {object}   props
 * @param {string}   props.lorryKey
 * @param {object[]} props.rows          the board rows on this lorry (with line_plan)
 * @param {object}   props.vehicle       vehicle spec (the Unknown vehicle's for one)
 * @param {object}   props.lorryDay      the lorry's stored layouts (version 2)
 * @param {boolean}  props.canEdit
 * @param {string}   props.readOnlyReason
 * @param {Function} props.onSave        ({ lorryDay?, lines, collectTimes }, { keepalive }) → Promise; throws with the reason
 * @param {Function} props.onClose
 */
function LorryDayWindow({ lorryKey, rows, vehicle, lorryDay, canEdit, readOnlyReason, onSave, onClose, unknownPayload, onSetUnknownPayload }) {
  const start = useMemo(() => startDraft(rows, lorryDay), [rows, lorryDay]);
  const [draft, setDraft] = useState(() => clone(start));
  // Saved as it changes (Cam, 23 Oct 2026: "can the 'Day view' auto save on
  // change, like the main view does?"). `saved` is what the server holds as
  // far as this window knows; each save sends the draft's difference from it.
  const saved = useRef(clone(start));
  const [save, setSave] = useState({ status: 'idle', error: null, at: null });
  const [closing, setClosing] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [message, setMessage] = useState(null);
  const latest = useRef({});
  latest.current = { draft, rows, canEdit, readOnlyReason, onSave };
  const timer = useRef(null);
  const queue = useRef(Promise.resolve(true));
  const inFlight = useRef(0);
  const pendingNow = () => !!payloadFor(latest.current.draft, saved.current, latest.current.rows);
  const unsaved = !!payloadFor(draft, saved.current, rows);
  const saving = save.status === 'saving';

  // A row put on this lorry (or taken off it) on the board while open: the
  // draft follows, so nothing it never had is sent or held back.
  useEffect(() => {
    const ids = Object.keys(start.lines);
    const add = (d) => {
      const next = { ...d, collect: { ...d.collect }, lines: { ...d.lines } };
      let moved = false;
      for (const id of ids) {
        if (!(id in next.lines)) { next.lines[id] = clone(start.lines[id]); next.collect[id] = start.collect[id]; moved = true; }
      }
      for (const id of Object.keys(next.lines)) {
        if (!ids.includes(id)) { delete next.lines[id]; delete next.collect[id]; moved = true; }
      }
      return moved ? next : d;
    };
    saved.current = add(saved.current);
    setDraft(d => add(d));
  }, [start]);

  /** One save of whatever differs now; true when everything is saved. */
  const saveOnce = async () => {
    const { draft: d, rows: rs, canEdit: may, readOnlyReason: why, onSave: send } = latest.current;
    const payload = payloadFor(d, saved.current, rs);
    if (!payload) {
      setSave(s => ({ status: s.at ? 'saved' : 'idle', error: null, at: s.at }));
      return true;
    }
    if (!may) {
      setSave({ status: 'error', error: why || 'This day cannot be changed now', at: null });
      return false;
    }
    const sent = clone(d);
    inFlight.current += 1;
    setSave(s => ({ ...s, status: 'saving', error: null }));
    try {
      await send(payload);
      // What the server holds now: the draft as sent (not later edits).
      saved.current = {
        collect: { ...saved.current.collect, ...Object.fromEntries(Object.keys(payload.collectTimes).map(id => [id, sent.collect[id]])) },
        lines: { ...saved.current.lines, ...Object.fromEntries(Object.keys(payload.lines).map(id => [id, sent.lines[id]])) },
        lorryDay: 'lorryDay' in payload ? sent.lorryDay : saved.current.lorryDay
      };
      inFlight.current -= 1;
      // Edits made while it was on its way are saved by their own pause.
      setSave({ status: pendingNow() ? 'pending' : 'saved', error: null, at: Date.now() });
      return true;
    } catch (error) {
      inFlight.current -= 1;
      setSave({ status: 'error', error: error.message || 'The lorry’s day could not be saved', at: null });
      return false;
    }
  };
  /** Save now (after any save already on its way). */
  const flush = () => {
    clearTimeout(timer.current);
    timer.current = null;
    queue.current = queue.current.then(saveOnce, saveOnce);
    return queue.current;
  };

  // Each change is saved a moment after the last one.
  useEffect(() => {
    if (!payloadFor(draft, saved.current, rows)) return undefined;
    if (!canEdit) {
      setSave({ status: 'error', error: readOnlyReason || 'This day cannot be changed now', at: null });
      return undefined;
    }
    setSave(s => (s.status === 'saving' ? s : { ...s, status: 'pending', error: null }));
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_AFTER_MS);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, canEdit]);

  // Never lose an edit to the window closing or the page going away: one
  // still to save goes now (after any on its way); a tab closing sends it with
  // keepalive, and asks first while anything is unsaved.
  useEffect(() => {
    const onHide = () => {
      const { draft: d, rows: rs, canEdit: may, onSave: send } = latest.current;
      const payload = payloadFor(d, saved.current, rs);
      if (payload && may) { try { send(payload, { keepalive: true }).catch(() => {}); } catch (_) { /* leaving anyway */ } }
    };
    const onBeforeUnload = (e) => {
      if (!pendingNow() && !inFlight.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
      clearTimeout(timer.current);
      // Unmounted with an edit still to save (another screen, another day):
      // saved after whatever is already on its way.
      if (pendingNow() && latest.current.canEdit) queue.current = queue.current.then(saveOnce, saveOnce);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dayRows = (d) => rows.map(r => ({ ...r, scheduled_time: d.collect[String(r.order_line_id)] }));
  const day = useMemo(() => {
    try {
      return buildLorryDay({ lorryKey, rows: dayRows(draft), vehicle, linePlans: draft.lines, lorryDay: draft.lorryDay });
    } catch (e) {
      console.warn('Lorry day: could not work the day out', e);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, rows, vehicle, lorryKey]);

  // Closing saves what is left first, and closes once it is saved; a save
  // that fails keeps the window open, the edit on screen, and asks.
  const requestClose = async () => {
    if (closing) return;
    if (!pendingNow() && !inFlight.current) { onClose(); return; }
    if (!canEdit && pendingNow()) { setConfirmDiscard(true); return; }
    setClosing(true);
    let ok = await flush();
    for (let i = 0; ok && pendingNow() && i < 3; i += 1) ok = await flush();
    setClosing(false);
    if (ok && !pendingNow()) onClose(); else setConfirmDiscard(true);
  };
  useEffect(() => {
    if (!confirmDiscard) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setConfirmDiscard(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirmDiscard]);

  const suggest = () => {
    try {
      const s = suggestLorryDay({ lorryKey, rows: dayRows(draft), vehicle, linePlans: draft.lines, lorryDay: draft.lorryDay });
      const filled = [];
      if (s.filled.compartments) filled.push(`${s.filled.compartments} product${s.filled.compartments === 1 ? '' : 's'} placed`);
      if (s.filled.layouts) filled.push(`${s.filled.layouts} door layout${s.filled.layouts === 1 ? '' : 's'} set`);
      if (!filled.length) { setMessage({ kind: 'info', text: 'Nothing blank to fill — every product already has a compartment.' }); return; }
      setDraft({ ...draft, lines: s.linePlans, lorryDay: s.lorryDay });
      setMessage({ kind: 'info', text: `Suggested: ${filled.join(', ')}. Nothing you had set was changed — check it; it is saved like any change.` });
    } catch (e) {
      setMessage({ kind: 'error', text: `Could not suggest a layout: ${e.message}` });
    }
  };

  const state = !canEdit && !unsaved
    ? { icon: 'visibility', tone: 'muted', text: `View only — ${readOnlyReason || 'this day cannot be changed now'}` }
    : save.status === 'error'
      ? { icon: 'error', tone: 'error', text: `Not saved: ${save.error} Your changes are still here.` }
      : saving || closing ? { icon: 'sync', tone: 'info', text: 'Saving…' }
        : unsaved ? { icon: 'schedule', tone: 'info', text: 'Saving in a moment…' }
          : save.at ? { icon: 'cloud_done', tone: 'ok', text: `All changes saved (${hhmm(save.at)})` }
            : { icon: 'cloud_done', tone: 'muted', text: 'Changes are saved as you make them' };
  const footer = (
    <div className="ldw-footer">
      <div className={`ldw-save-state ldw-save-${state.tone}`} role="status" aria-live="polite">
        <span className="material-symbols-rounded">{state.icon}</span>
        <span>{state.text}</span>
      </div>
      <div className="buttons slideout">
        {save.status === 'error' && canEdit && (
          <div><button className="btn-slideout" onClick={() => flush()} disabled={saving || closing} title="Save the changes again">
            <div className="icon-wrapper"><span className="material-symbols-rounded">refresh</span></div>
            <span className="btn-label">Try again</span>
          </button></div>
        )}
        <div><button className={`btn-slideout${canEdit && day && day.body.count ? '' : ' btn-disabled'}`} disabled={!canEdit || closing || !day || !day.body.count}
          title={!canEdit ? readOnlyReason : (day && day.body.count ? 'Fill in what is blank — compartments and door layouts — without changing anything you set' : 'This vehicle has no compartments')}
          onClick={suggest}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">auto_fix_high</span></div>
          <span className="btn-label">Suggest</span>
        </button></div>
        <div><button className="btn-slideout" onClick={requestClose} disabled={closing}
          title={unsaved || saving ? 'Save what is left, then close' : 'Close this lorry’s day'}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">{closing ? 'Saving…' : 'Close'}</span>
        </button></div>
      </div>
    </div>
  );

  const driver = (rows.find(r => String(r.driver || '').trim()) || {}).driver;
  const title = `${lorryLabel(lorryKey)}${driver && !String(lorryKey).startsWith('Unknown::') ? ` · ${driver}` : ''}`;
  return (
    <Modal isOpen onClose={requestClose} closeOnEscape={!confirmDiscard && !closing} title={title} size="xl" className="lorry-day-window" footer={footer}>
      {day ? (
        <LorryDayBody
          day={day} draft={draft} setDraft={setDraft} vehicle={vehicle}
          canEdit={canEdit} readOnlyReason={readOnlyReason} saving={saving || closing} dirty={unsaved || save.status === 'error'}
          message={message} setMessage={setMessage}
          unknownPayload={unknownPayload} onSetUnknownPayload={onSetUnknownPayload}
        />
      ) : <p className="ldw-message ldw-message-error">This lorry’s day could not be worked out. Close the window and try again.</p>}
      <ConfirmDialog
        isOpen={confirmDiscard}
        title="Close without saving?"
        message={save.status === 'error' && save.error
          ? `The last changes to this lorry’s day could not be saved: ${save.error} Close the window and lose them, or keep editing and try again?`
          : `The last changes to this lorry’s day are not saved${!canEdit && readOnlyReason ? ` (${readOnlyReason})` : ''}. Close the window and lose them?`}
        confirmText="Close without saving"
        cancelText="Keep editing"
        onConfirm={() => { setConfirmDiscard(false); saved.current = clone(latest.current.draft); onClose(); }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </Modal>
  );
}

export default LorryDayWindow;
export { startDraft, compartmentLabel, payloadFor };
